import { defineStore } from 'pinia';
import { http } from '@/system/http';
import Apps from '@/core/Apps';
import store from '@/system/store';

export const useTasksStore = defineStore('tasks', {
    state: () => ({
        tasks: {},
        taskCategories: [],
        taskTypeToLoad: {},
        filteredTasks: {},
        taskFilters: [],
        tasksLoading: false,
    }),

    getters: {
        canSeeTasks() {
            return store.getters['apps/isAppEnabled'](Apps.Tasks);
        },
        
        allTasksHaveLoaded(state) {
            return state.taskCategories.length > 0
                && state.taskCategories.every(category => category.finished_loading);
        },
        
        tasksEmptyState() {
            return !this.allTasksHaveLoaded || (this.noTasksExist && this.canSeeTasks) ? 'noTasks' : '';
        },
        
        filteredTaskCategories(state) {
            return state.taskCategories.filter(
                category => (category.type !== 'Today' || category.type !== 'Important') && category.type !== 'Task'
            )
                .filter(category => state.tasks[category.model]?.data.length > 0)
                .map(category => ({ id: category.model, text: category.filter_string }));
        },
        
        noTasksExist(state) {
            return Object.values(state.filteredTasks).length > 0
                && Object.values(state.filteredTasks).every(task => task.meta?.total === 0);
        },
        
        anyTaskFiltersExist(state) {
            return this.filteredTaskCategories.length > 0;
        },
    },

    actions: {
        async getTasks(fromLoadMore = false, alreadyRetried = false, clientId = null) {
            if (!this.canSeeTasks) {
                return;
            }

            // Generate hash for request to check for duplicates
            const requestHash = clientId || 'global';
            
            // Skip if requesting the same data and not loading more
            if (this.lastTasksRequestHash === requestHash && !fromLoadMore) {
                return;
            }
            
            // Remember this request hash
            this.lastTasksRequestHash = requestHash;

            if (fromLoadMore) {
                this.updateTaskCategoryLoadingState(this.taskTypeToLoad, false);
                await this.getIndividualTasks(this.taskTypeToLoad, true, false, false, false, clientId);
            } else {
                this.tasks = {};
                this.tasksLoading = true;

                try {
                    const { data } = await http.get('/app/settings/apps/tasks/categories');
                    this.taskCategories = data;
                    
                    const taskPromises = this.taskCategories.map(category => {
                        return this.getIndividualTasks(category, false, false, false, false, clientId);
                    });
                    await Promise.all(taskPromises);
                } catch (error) {
                    console.error('Error fetching tasks:', error);
                    if (!alreadyRetried) {
                        this.getTasks(fromLoadMore, true, clientId);
                    }
                } finally {
                    this.updateTaskCategoryLoadingState(this.taskTypeToLoad, true);
                    this.tasksLoading = false;
                }
            }
        },
        
        taskCategoryFinishedLoading(category) {
            return this.taskCategories.find(taskCategory => taskCategory.model === category)?.finished_loading;
        },

        getCurrentPageForCategory(category) {
            if (Object.keys(this.tasks).length === 0) {
                return 1;
            }

            return this.tasks[category.model]?.meta?.current_page || 1;
        },

        async getIndividualTasks(
            category,
            fromLoadMore = false,
            reloadToday = false,
            reloadImportant = false,
            disableLoadingState = false,
            clientId = null,
        ) {
            if (!this.canSeeTasks) {
                return;
            }

            if (fromLoadMore) {
                this.updateTaskCategoryLoadingState(category, false, false);
            }

            try {
                const { data } = await http.get(`/app/settings/apps/tasks/${category.type}`, {
                    params: {
                        client_id: clientId,
                        page: this.getCurrentPageForCategory(category) + (fromLoadMore ? 1 : 0),
                    },
                });
                
                const tasks = Object.assign({}, this.tasks);

                if (tasks[category.model] && fromLoadMore) {
                    tasks[category.model].data = [...this.tasks[category.model].data, ...data.data];
                    tasks[category.model].links = data.links;
                    tasks[category.model].meta = data.meta;
                } else {
                    tasks[category.model] = data;
                }

                this.tasks = tasks;

                if (reloadToday) {
                    await this.getIndividualTasks(
                        { model: 'Today', type: 'Today' },
                        false,
                        false,
                        false,
                        disableLoadingState,
                        clientId
                    );
                }

                if (reloadImportant) {
                    await this.getIndividualTasks(
                        { model: 'Important', type: 'Important' },
                        false,
                        false,
                        false,
                        disableLoadingState,
                        clientId
                    );
                }

                this.updateTaskCategoryLoadingState(category, true);
                this.taskTypeToLoad = {};
            } catch (error) {
                console.error('Error fetching individual tasks:', error);
            }
        },

        organisedTasks(filters = [], clientId = null) {
            const priority = { Important: 1, Today: 2 };

            let tasks = Object.keys(this.tasks)
                .sort(
                    (firstCategory, secondCategory) => (priority[firstCategory] || 99)
                        - (priority[secondCategory] || 99),
                )
                .reduce((sortedTasks, key) => {
                    sortedTasks[key] = this.tasks[key];
                    return sortedTasks;
                }, {});

            if (filters.length > 0) {
                tasks = Object.entries(tasks)
                    .filter(([key, task]) => filters.some(filter => key === filter.id))
                    .reduce(
                        (result, [key, task]) => {
                            result[key] = task;
                            return result;
                        },
                        {},
                    );
            }

            if (clientId) {
                return Object.entries(tasks)
                    .filter(([key, task]) => task.data.some(task => task.client?.id === clientId))
                    .reduce(
                        (result, [key, task]) => {
                            result[key] = task;
                            return result;
                        },
                        {},
                    );
            }

            return tasks;
        },

        updateTaskCategoryLoadingState(category, loaded) {
            if (!category || !category.model) return;
            
            this.taskCategories.forEach((taskCategory) => {
                if (taskCategory.model === category.model) {
                    taskCategory.finished_loading = loaded;
                }
            });
        },
        
        handleRefreshTaskList(clientId = null) {
            this.tasks = {};
            this.lastTasksRequestHash = null;
            this.getTasks(false, false, clientId);
        },
        
        handleLoadTaskCategory(category, fromLoadMore = false, reloadImportant = false, clientId = null) {
            this.taskTypeToLoad = category;
            this.getIndividualTasks(category, fromLoadMore, !fromLoadMore, reloadImportant, true, clientId);
        },
        
        updateFilteredTasks(clientId = null) {
            this.filteredTasks = this.organisedTasks(this.taskFilters, clientId);
        },

        resetState() {
            this.tasks = {};
            this.taskCategories = [];
            this.taskTypeToLoad = {};
            this.filteredTasks = {};
            this.taskFilters = [];
            this.tasksLoading = false;
            this.lastTasksRequestHash = null;
        },
    }
});